import React, { useEffect, useMemo, useState } from "react";
import {
    View,
    Text,
    StyleSheet,
    KeyboardAvoidingView,
    Platform,
} from "react-native";
import { useTranslation } from "react-i18next";
import ShieldIcon from "../../../../../../../assets/images/Shield.svg";
import Colors from "../../../../constants/colors";
import { ComponentStyles } from "../../../../constants/componentStyles";
import FloatingTextInputComponent, {
    addressFieldTextInputProps,
} from "../../common/FloatingTextInputComponent";
import {
    validateField,
    getFieldMax,
    getFieldMin,
} from "../../../../helper/fieldHelper";
import { normalizeUsCountrySubdivisionCode } from "../../../../helper/mapiSubdivisionCode";
import { useJourneyStore } from "../store/sendmoneyJourneyStore";
import { useWorkflowNavigation } from "../hooks/useWorkflowNavigation";
import { useStepAction } from "../hooks/useStepAction";
import type { FieldInfo } from "../models/fieldInfo";
import type { Address } from "../models/profileSearch";

export function SenderAddressScreen() {
    const { t } = useTranslation();
    const fieldInfo =
        useJourneyStore(
            (state) => state.journeyData.transactionFieldsSendResponse?.fieldInfo
        ) ?? [];
    const senderAddress = useJourneyStore(
        (state) => state.journeyData.senderAddress
    );
    const updateJourneyData = useJourneyStore((state) => state.updateJourneyData);
    const { goNext } = useWorkflowNavigation();

    const senderAddressFields = useMemo(() => {
        return fieldInfo
            .filter((f: FieldInfo) => f.field.startsWith("sender.address."))
            .sort(
                (a, b) =>
                    Number(a.displayOrder ?? 0) - Number(b.displayOrder ?? 0)
            );
    }, [fieldInfo]);

    const [values, setValues] = useState<Record<string, string>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        setValues((prev) => {
            const next = { ...prev };
            senderAddressFields.forEach((f) => {
                if (next[f.field] === undefined) {
                    next[f.field] = senderAddress[f.field as keyof Address] ?? "";
                }
            });
            return next;
        });
    }, [senderAddressFields, senderAddress]);

    const getLabel = (field: FieldInfo) => {
        const label = field.fieldLabel || field.field;
        if (field.required) return label;
        return `${label} (optional)`;
    };

    const handleNext = () => {
        const newErrors: Record<string, string> = {};
        senderAddressFields.forEach((f) => {
            newErrors[f.field] = validateField(
                fieldInfo,
                f.field,
                values[f.field] ?? ""
            );
        });
        setErrors(newErrors);

        if (Object.values(newErrors).some(Boolean)) {
            return;
        }
        // take a look at this. we are converting the values to an unknown type and then to an Address type.
        updateJourneyData({ senderAddress: values as unknown as Address });
        goNext();
    };

    useStepAction(handleNext);

    return (
        <KeyboardAvoidingView
            style={styles.container}
            behavior={Platform.OS === "ios" ? "padding" : undefined}
            testID="sender-address-screen"
        >
            <Text style={styles.title}>{t("SENDER_ADDRESS_TITLE")}</Text>
            <Text style={styles.subtitle}>{t("SENDER_ADDRESS_SUBTITLE")}</Text>

            {senderAddressFields.map((field, index) => {
                const max = getFieldMax(fieldInfo, field.field);
                const min = getFieldMin(fieldInfo, field.field);
                return (
                    <View key={field.field}>
                        <FloatingTextInputComponent
                            testID={`sendMoney-${field.field}`}
                            label={getLabel(field)}
                            value={values[field.field] ?? ""}
                            isAutoFocus={index === 0}
                            maxLength={max ? Number(max) : undefined}
                            minLength={min ? Number(min) : undefined}
                            onChangeText={(val) => {
                                const nextVal =
                                    field.field === "sender.address.countrySubdivisionCode"
                                        ? normalizeUsCountrySubdivisionCode(val)
                                        : val;
                                setValues((prev) => ({ ...prev, [field.field]: nextVal }));
                                if (errors[field.field]) {
                                    setErrors((prev) => ({ ...prev, [field.field]: "" }));
                                }
                            }}
                            errorText={errors[field.field]}
                            {...addressFieldTextInputProps}
                        />
                        {field.required && field.helpTextLong ? (
                            <Text style={styles.helpText}>{field.helpTextLong}</Text>
                        ) : null}
                    </View>
                );
            })}

            <View style={styles.hintBox}>
                <ShieldIcon width={24} height={24} />
                <Text style={styles.hintText}>
                    {t("YOUR_INFO_IS_ALWAYS_KEPT_PRIVATE")}
                </Text>
            </View>

        </KeyboardAvoidingView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: Colors.white,
        paddingHorizontal: 24,
    },
    title: {
        fontFamily: ComponentStyles.textFamily,
        fontSize: ComponentStyles.titleFont,
        fontWeight: "500",
        color: Colors.titleTextColor,
        lineHeight: 34,
        marginBottom: 8,
    },
    subtitle: {
        fontFamily: ComponentStyles.textFamily,
        fontSize: ComponentStyles.subTitleFont,
        fontWeight: "400",
        lineHeight: 24,
        color: Colors.subTitleColor,
        marginBottom: 24,
    },
    helpText: {
        fontFamily: ComponentStyles.textFamily,
        fontSize: 12,
        fontWeight: "400",
        color: Colors.subTitleColor,
        lineHeight: 16,
        marginTop: 4,
        marginBottom: 8,
    },
    hintBox: {
        flexDirection: "row",
        alignItems: "center",
        borderWidth: 1,
        borderColor: Colors.titleTextColor,
        borderRadius: 10,
        padding: 16,
        marginTop: 24,
        backgroundColor: Colors.white,
        marginBottom: 24,
    },
    hintText: {
        fontFamily: ComponentStyles.textFamily,
        fontSize: 14,
        fontWeight: "400",
        color: Colors.darkGrey,
        lineHeight: 18,
        paddingLeft: 16,
    },
});
